// Structured-address editor for fieldType "address". Six free-text subfields
// (street, unit, city, province/state, postal, country) serialized as a single
// JSON object-string through the field's one value channel. Extracted from
// GenericCategoryPage so EditableCell (table + expandable drawer) and
// WizardStepField can reuse it; the card layout still renders it directly.
//
// Value (de)serialization lives in @shared/address so the server consumers share
// the exact same shape.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CELL_BOX } from "@/components/interview/InterviewCells";
import { type AddressData, EMPTY_ADDRESS, parseAddress, formatAddressSummary } from "@shared/address";
import { reportPlacesFailure, loadPlacesLibrary, placeToAddressParts } from "@/lib/google-maps-loader";

// Canada bounding box — biases (does NOT restrict) autocomplete results toward
// Canadian addresses via locationBias. International addresses still resolve;
// they just rank below local matches. (locationBias biases only — unlike
// includedRegionCodes, which would hard-filter.)
const CANADA_BIAS = { north: 83.23324, south: 41.6751050889, east: -52.6480987209, west: -141.00187 };

const MIN_QUERY_LEN = 3;

type Suggestion = { prediction: any; label: string };

/**
 * s235 (D-235.34; walk item 11, D-235.26) — THE INTERVIEW LOOK, default OFF. The dashboard keeps its `<Input>`s; inside
 * the interview the six boxes draw as the board draws them, in the board's words, with rule 28's map pin on the street:
 *   • `"field"` — Personal details' home address (`Main.dc.html:101-115`): the street box the full width, 50px, then
 *     Unit / suite · City · Province · Postal code · Country on one row (`150px minmax(220px, 1fr) 180px 170px 180px`),
 *     every box `#FEFCF8` (CELL_BOX, the interview's box).
 *   • `"card"` — the contact card's open address (`Main.dc.html:469-471`): street 2fr + "Unit or suite" 1fr, then City ·
 *     Province · Postal code · Country, four across, every box cream `#FAF7F1` like the card's other boxes.
 */
export type AddressInterviewLook = "field" | "card" | "line";

const INTERVIEW_WORDS = {
  field: { street: "Street address", street2: "Unit / suite", city: "City", state: "Province", postal: "Postal code", country: "Country" },
  card: { street: "Street address", street2: "Unit or suite", city: "City", state: "Province", postal: "Postal code", country: "Country" },
} as const;

/**
 * s235 (D-235.97, Reade "agree to all"; supersedes D-235.26's six always-open boxes) — `"line"`: Personal details' home
 * address is ONE line (`Main.dc.html`, the home address block). Typing in it asks Places for addresses (the same path as
 * the street box above: three letters, a 250ms pause, our own list); picking one fills all six parts behind it, and the
 * line then reads the whole address on one line (`formatAddressSummary`, as chapter two's address cell does). The six
 * parts open only on Edit, with Done to close (rule 49) — the unit is edited there. Without Places (no key, the script
 * fails, a refused key) the line is a plain box: what is typed is kept as the street, and Edit fills in the rest.
 */
const LINE_WORDS = { edit: "Edit", done: "Done" } as const;

export function AddressInput({ value, onChange, onBlur, readOnly, fieldId, interviewLook, linePlaceholder, lineScrolls = false }: {
  value: string; onChange: (v: string) => void; onBlur: () => void; readOnly?: boolean; fieldId: string;
  /** s235 (D-235.34): the interview's look; absent = the dashboard's, unchanged. */
  interviewLook?: AddressInterviewLook;
  /** s235 (D-235.97): the `"line"` look's empty words — the screen passes the board's own. */
  linePlaceholder?: string;
  /** s237 Contacts PR F: the "line" look's address SCROLLS in its box instead of ending in "…" (D-223.14: a scrolling input is
   *  reported, never failed) — the Contacts card at 790px is narrower than a long address. Default OFF: the interview's line
   *  keeps its ellipsis. Edit still opens all six parts. */
  lineScrolls?: boolean;
}) {
  const addr: AddressData = useMemo(() => {
    const parsed = parseAddress(value);
    if (parsed) return parsed;
    // Legacy / plain-text value — e.g. a field just flipped text→address whose
    // existing value is a plain string, or an onboarding-saved address. Surface
    // it in `street` so it stays visible and editable instead of rendering as
    // empty boxes a stray keystroke would overwrite. Empty/whitespace → blank.
    return { ...EMPTY_ADDRESS, street: value && value.trim() ? value : "" };
  }, [value]);

  const update = (key: keyof AddressData, v: string) => {
    onChange(JSON.stringify({ ...addr, [key]: v }));
  };

  // Google Places autocomplete on the Street box, via the new
  // AutocompleteSuggestion API + our own dropdown (see google-maps-loader for
  // why not the legacy widget). On selection we fetch the place's structured
  // components and fill ALL subfields at once, preserving the user-typed unit
  // (street2). Latest addr/onChange are read through refs so async callbacks
  // commit against current state.
  const placesRef = useRef<any>(null);
  const sessionTokenRef = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addrRef = useRef(addr);
  addrRef.current = addr;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // s235 (D-235.97): the `"line"` look — what is being typed in the line (null: the line reads the address), and
  // whether its six parts are open.
  const [query, setQuery] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    loadPlacesLibrary().then((lib) => {
      // null → no key/script (graceful fallback: plain input, no dropdown).
      if (!cancelled && lib) placesRef.current = lib;
    });
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, [readOnly]);

  const fetchSuggestions = async (input: string) => {
    if (input.trim().length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    // Lazily resolve the library if the mount effect hasn't populated it yet
    // (e.g. the user typed before the bootstrap finished loading).
    let lib = placesRef.current;
    if (!lib) {
      lib = await loadPlacesLibrary();
      if (lib) placesRef.current = lib;
    }
    if (!lib) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (!sessionTokenRef.current) sessionTokenRef.current = new lib.AutocompleteSessionToken();
    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      sessionToken: sessionTokenRef.current,
      locationBias: CANADA_BIAS,
    })
      .then((res: any) => {
        const list: Suggestion[] = (res?.suggestions ?? [])
          .filter((s: any) => s.placePrediction)
          .map((s: any) => ({ prediction: s.placePrediction, label: s.placePrediction.text?.text ?? "" }));
        setSuggestions(list);
        setActiveIndex(-1);
        setOpen(list.length > 0);
      })
      .catch((err: unknown) => {
        // s227 (D9 part 2): a refused key is reported once in the console; the user still sees a plain box.
        reportPlacesFailure(err);
        setSuggestions([]);
        setOpen(false);
      });
  };

  const handleStreetChange = (v: string) => {
    update("street", v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const selectSuggestion = async (s: Suggestion) => {
    setOpen(false);
    setQuery(null); // the `"line"` look: the pick replaces what was typed
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    try {
      const place = s.prediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const parts = placeToAddressParts(place);
      onChangeRef.current(
        JSON.stringify({
          ...addrRef.current, // keep street2 (unit/suite) the user entered
          ...parts,
          street: parts.street || addrRef.current.street,
        }),
      );
      // Defer the save one tick. onChange writes the new value into the parent's
      // local-edit state asynchronously; the parent's blur handler then READS
      // that state to persist it (and cancels the change's own debounced save).
      // Calling blur synchronously would read stale/empty edits and persist
      // nothing. After a tick the edit has committed and onBlurRef points at a
      // fresh closure over it. Works for both card (handleFieldBlur) and table
      // (handleTableCellBlur), which share this read-local-state-on-blur shape.
      setTimeout(() => onBlurRef.current(), 0);
    } catch {
      // Network/permission error — leave the typed text as-is (no dropdown).
    }
    sessionTokenRef.current = null; // a new session begins after each selection
  };

  const handleStreetKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleStreetBlur = () => {
    onBlur(); // field autosave
    // Defer close so a suggestion mousedown (which preventDefaults the blur)
    // and outside clicks both resolve correctly.
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  if (readOnly) {
    const parts = [addr.street, addr.street2, addr.city, addr.state, addr.postal, addr.country].filter(Boolean);
    return <span className="text-sm">{parts.length ? parts.join(", ") : <span className="text-muted-foreground">—</span>}</span>;
  }

  const suggestionList = open && suggestions.length > 0 && (
    <ul className="address-suggestions" role="listbox" data-testid={`address-suggestions-${fieldId}`}>
      {suggestions.map((s, i) => (
        <li
          key={i}
          role="option"
          aria-selected={i === activeIndex}
          className={`address-suggestion-item${i === activeIndex ? " is-active" : ""}`}
          // mousedown (not click) + preventDefault keeps focus on the input
          // so the blur-driven close doesn't fire before selection lands.
          onMouseDown={(e) => {
            e.preventDefault();
            selectSuggestion(s);
          }}
          onMouseEnter={() => setActiveIndex(i)}
        >
          {s.label}
        </li>
      ))}
      <li className="address-suggestions-attribution" aria-hidden="true">Powered by Google</li>
    </ul>
  );

  if (interviewLook === "line") {
    const words = INTERVIEW_WORDS.field;
    const summary = formatAddressSummary(value);
    const placeholder = linePlaceholder ?? words.street;
    const small = cn(CELL_BOX, "px-3 text-[14px]");
    const part = (key: Exclude<keyof AddressData, "street">) => (
      <input value={addr[key]} onChange={(e) => update(key, e.target.value)} onBlur={onBlur} placeholder={words[key]}
        aria-label={words[key]} autoComplete="off" className={small} data-testid={`address-${key}-${fieldId}`} />
    );
    // Leaving the line after typing without a pick: what was typed is the street (an empty line clears the address).
    const lineBlur = () => {
      if (query !== null) {
        const q = query.trim();
        if (q !== summary.trim()) onChange(q ? JSON.stringify({ ...EMPTY_ADDRESS, street: q }) : "");
        setQuery(null);
      }
      handleStreetBlur();
    };
    return (
      <div className="flex flex-col gap-2.5" data-testid={`input-card-${fieldId}`} data-look="line">
        <div className="flex items-center gap-2.5">
          <div className="relative min-w-0 flex-1">
            <MapPin className="pointer-events-none absolute left-4 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-warm-taupe"
              aria-hidden="true" data-testid={`address-pin-${fieldId}`} />
            <input
              value={query ?? summary}
              onChange={(e) => {
                const v = e.target.value;
                setQuery(v);
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
              }}
              onKeyDown={handleStreetKeyDown}
              onBlur={lineBlur}
              placeholder={placeholder}
              aria-label={placeholder}
              title={summary || undefined}
              className={cn(CELL_BOX, "h-[50px] pl-[43px] pr-4 text-[16px]", !lineScrolls && "truncate")}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              data-testid={`address-line-${fieldId}`}
            />
            {suggestionList}
          </div>
          {summary && (
            <button type="button" aria-expanded={editing} onClick={() => { if (editing) onBlur(); setEditing(!editing); }}
              className="flex min-h-11 shrink-0 items-center text-[13px] text-sage-600 hover:text-sage-800" data-testid={`address-edit-${fieldId}`}>
              {LINE_WORDS.edit}
            </button>
          )}
        </div>
        {editing && (
          <div className="flex flex-col gap-2.5" data-testid={`address-parts-${fieldId}`}>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-warm-taupe" aria-hidden="true" />
              <input value={addr.street} onChange={(e) => update("street", e.target.value)} onBlur={onBlur} placeholder={words.street}
                aria-label={words.street} autoComplete="off" className={cn(small, "pl-[34px]")} data-testid={`address-street-${fieldId}`} />
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-[150px_minmax(220px,1fr)_180px_170px_180px]">
              {part("street2")}{part("city")}{part("state")}{part("postal")}{part("country")}
            </div>
            <div>
              <button type="button" onClick={() => { onBlur(); setEditing(false); }}
                className="h-11 rounded-[8px] bg-sage-600 px-5 text-[14px] text-warm-white hover:bg-sage-800" data-testid={`address-done-${fieldId}`}>
                {LINE_WORDS.done}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (interviewLook) {
    const words = INTERVIEW_WORDS[interviewLook];
    const field = interviewLook === "field";
    const box = cn(CELL_BOX, "px-3 text-[14px]", !field && "bg-warm-cream");
    const part = (key: Exclude<keyof AddressData, "street">) => (
      <input value={addr[key]} onChange={(e) => update(key, e.target.value)} onBlur={onBlur} placeholder={words[key]}
        aria-label={words[key]} autoComplete="off" className={box} data-testid={`address-${key}-${fieldId}`} />
    );
    const street = (
      <div className="relative">
        <MapPin className={cn("pointer-events-none absolute top-1/2 -translate-y-1/2 text-warm-taupe", field ? "left-4 h-[17px] w-[17px]" : "left-3 h-[14px] w-[14px]")}
          aria-hidden="true" data-testid={`address-pin-${fieldId}`} />
        <input
          value={addr.street}
          onChange={(e) => handleStreetChange(e.target.value)}
          onKeyDown={handleStreetKeyDown}
          onBlur={handleStreetBlur}
          placeholder={words.street}
          aria-label={words.street}
          className={cn(box, field ? "h-[50px] pl-[43px] pr-4 text-[16px]" : "pl-[34px]")}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          data-testid={`address-street-${fieldId}`}
        />
        {suggestionList}
      </div>
    );
    return field ? (
      <div className="flex flex-col gap-2.5" data-testid={`input-card-${fieldId}`} data-look="field">
        {street}
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-[150px_minmax(220px,1fr)_180px_170px_180px]">
          {part("street2")}{part("city")}{part("state")}{part("postal")}{part("country")}
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-2.5" data-testid={`input-card-${fieldId}`} data-look="card">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">{street}{part("street2")}</div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">{part("city")}{part("state")}{part("postal")}{part("country")}</div>
      </div>
    );
  }

  // Responsive grid: at narrow widths (popover, card, wizard — below lg) every
  // field is full-width and stacks, since the lg:col-span-* classes don't apply.
  // At lg (≥1024px, e.g. the Properties drawer at ~1500px) the 4-column grid
  // activates: Street (span 3) + Unit (span 1) on row 1; City / Province / Postal
  // / Country one column each on row 2.
  return (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-4" data-testid={`input-card-${fieldId}`}>
      <div className="relative lg:col-span-3">
        <Input
          value={addr.street}
          onChange={(e) => handleStreetChange(e.target.value)}
          onKeyDown={handleStreetKeyDown}
          onBlur={handleStreetBlur}
          placeholder="Street address"
          className="text-sm"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {suggestionList}
      </div>
      <Input value={addr.street2} onChange={(e) => update("street2", e.target.value)} onBlur={onBlur} placeholder="Unit / suite (optional)" className="text-sm lg:col-span-1" />
      <Input value={addr.city} onChange={(e) => update("city", e.target.value)} onBlur={onBlur} placeholder="City" className="text-sm lg:col-span-1" />
      <Input value={addr.state} onChange={(e) => update("state", e.target.value)} onBlur={onBlur} placeholder="Province/State" className="text-sm lg:col-span-1" />
      <Input value={addr.postal} onChange={(e) => update("postal", e.target.value)} onBlur={onBlur} placeholder="Postal Code" className="text-sm lg:col-span-1" />
      <Input value={addr.country} onChange={(e) => update("country", e.target.value)} onBlur={onBlur} placeholder="Country" className="text-sm lg:col-span-1" />
    </div>
  );
}
